// signOutThisDevice — the ONE way a shared device is left for the next person.
//
// Three callers, deliberately one path so they cannot drift apart:
//   - useDeviceIdleSignOut — a paired app device left idle past its window;
//   - KioskIdleLogout      — a `?kiosk=1` tablet left idle 15 minutes;
//   - ConsentGate          — the Sign out button on the full-screen prompt, for
//                            the person who will not or cannot accept now.
//
//   paired app device → its local capture session ends, then the worker's
//                        Supabase session, then back to the badge + face lock
//                        screen (/device-signin) — never /login.
//   anything else      → every session + the offline identity snapshots
//                        (signOutEverywhere), then a hard reload to a clean
//                        boot, which lands on sign-in.
//
// ⚠️ IT MUST WORK WITH NO CONNECTION. supabase-js 2.108's signOut() POSTs
// /logout first; when that fetch fails it RETURNS the error and leaves the
// stored session in place (GoTrueClient._signOut only removes it after the call
// succeeds, or on a 401/403/404), and on a dead link the POST can simply hang.
// A shared device is the device on the bad link, and the consent prompt's Sign
// out exists for exactly the moment acceptance cannot be recorded for want of a
// connection. So the server call is bounded, and if the session is still on
// disk afterwards it is dropped from storage and the page reloaded hard — a
// fresh boot reads storage (App.tsx hasStoredSession) and finds nobody.

import type { NavigateFunction } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { isPairedDevice } from '@/lib/deviceSession'
import { endLocalSession } from '@/lib/deviceLocalSession'
import {
  signOutEverywhere, hasStoredSession, dropStoredSession, clearOfflineIdentity,
} from '@/lib/offlineSession'
import { withTimeout } from '@/lib/saveTimeout'

/** How long the server sign-out may take before we stop waiting for it. */
export const SIGN_OUT_WAIT_MS = 8_000

/** The lock screen a paired device returns to. */
export const DEVICE_LOCK_PATH = '/device-signin'

// Bounded, never throws. Not save telemetry: a sign-out is not a save.
async function bounded(op: () => PromiseLike<unknown>): Promise<void> {
  try { await withTimeout(op(), 'sign-out', SIGN_OUT_WAIT_MS, undefined, false) } catch { /* fall through to the local drop */ }
}

export async function signOutThisDevice(navigate: NavigateFunction): Promise<void> {
  if (isPairedDevice()) {
    // Local session first: if the server call hangs, the tablet must not still
    // be showing the last worker as signed in locally while it waits.
    endLocalSession()
    await bounded(() => supabase.auth.signOut())
    if (!hasStoredSession()) {
      navigate(DEVICE_LOCK_PATH, { replace: true })
      return
    }
    dropStoredSession()
    window.location.assign(DEVICE_LOCK_PATH)
    return
  }
  await bounded(() => signOutEverywhere())
  if (hasStoredSession()) dropStoredSession()
  clearOfflineIdentity()
  // Hard reload to a clean boot so no in-memory state from the previous person
  // survives for the next one.
  window.location.assign('/')
}
